import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Input from '@zg-rentals/multibrand-ui/lib/Input';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Modal from '@common/components/Modal';
import FaqComponent from '@common/components/FaqComponent';
import SpinnerContainer from '@common/components/SpinnerContainer';
import FaqConstants from '@rental-manager/constants/FaqConstants';
import userPersonas from '@common/constants/userPersonas';
import { validateAllInputs, validateTenantSelected } from './validation';
import type { ILeasesForPropertyModel } from '@rental-manager/models/Types';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Label from '@zg-rentals/multibrand-ui/lib/Label';
import SpacerRow from '@zg-rentals/multibrand-ui/lib/SpacerRow';
import Section from '@rental-manager/components/Section';
import DebouncedButton from '@common/components/DebouncedButton';
import { detectCombinedNames } from '@common/utils/detectCombinedNames';
import errorMessages from '@common/constants/errorMessages';

interface Props {
  tenantFirstName: string;
  tenantLastName: string;
  tenantEmail: string;
  onSubmit: ({ tenantFirstName, tenantLastName, emailAddress, rentTermsKey }) => any;
  onClose: () => void;
  onCancel: () => void;
  trackValidationError: (...args: any) => any;
  rentTermsKey: string;
  isRentGuaranteeEnabled: boolean;
  selectedLease: ILeasesForPropertyModel;
  selectedTenantId: number;
}

interface State {
  tenantFirstName: { value: string; error: string };
  tenantLastName: { value: string; error: string };
  emailAddress: { value: string; error: string };
  tenantId: { value: number; error: string };
  isLoading: boolean;
}
class EditTenantInfoModal extends React.Component<Props, State> {
  static defaultProps = {
    tenantFirstName: '',
    tenantLastName: '',
    tenantEmail: '',
    selectedTenantId: -1,
    onSubmit: () => {},
    onClose: () => {},
    onCancel: () => {},
    trackValidationError: () => {},
  };

  constructor(props) {
    super(props);
    const isSingleTenantOnLease = props.selectedLease?.leaseTenants?.length === 1;
    this.state = {
      tenantFirstName: {
        value: props.tenantFirstName,
        error: detectCombinedNames(props.tenantFirstName) ? errorMessages.COMBINED_NAMES_ERROR_TEXT : '',
      },
      tenantLastName: {
        value: props.tenantLastName,
        error: detectCombinedNames(props.tenantLastName) ? errorMessages.COMBINED_NAMES_ERROR_TEXT : '',
      },
      emailAddress: {
        value: props.tenantEmail,
        error: '',
      },
      tenantId: {
        value: isSingleTenantOnLease ? 0 : props.selectedTenantId,
        error: '',
      },
      isLoading: false,
    };
  }

  onInputChange = (e) => {
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    // @ts-expect-error  will  be addressed in RJS upgrade
    this.setState({
      [key]: {
        value,
        error: '',
      },
    });
  };

  onInputChangeName = (e): void => {
    const key = e.target.dataset.statekey;
    const value = e.target.value;
    let error = '';
    if (detectCombinedNames(value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT;
    }
    // @ts-expect-error  will  be addressed in RJS upgrade
    this.setState({
      [key]: {
        value,
        error,
      },
    });
  };

  renderTenantOptions = () => {
    const { selectedLease } = this.props;
    const defaultOption = (
      <option key={-1} disabled value="-1">
        Select one
      </option>
    );
    const options = selectedLease?.leaseTenants?.map((leaseTenant, index) => {
      return (
        <option key={index} value={index}>
          {leaseTenant.firstName}&nbsp;{leaseTenant.lastName}
        </option>
      );
    });

    return [defaultOption, ...options];
  };

  onChangeSelect = (e) => {
    const selectedOption = e.currentTarget.options[e.currentTarget.selectedIndex];
    const value = parseInt(selectedOption.getAttribute('value'), 10);

    this.setState({
      tenantId: {
        value,
        error: '',
      },
    });
  };

  toggleIsLoading = (state = !this.state.isLoading) => {
    this.setState({ isLoading: state });
  };

  handleSubmit = (e) => {
    e.preventDefault();

    if (validateAllInputs(this)) {
      return Promise.resolve()
        .then(() => this.toggleIsLoading(true))
        .then(() => {
          return this.props.onSubmit({
            tenantFirstName: this.state.tenantFirstName.value.trim(),
            tenantLastName: this.state.tenantLastName.value.trim(),
            emailAddress: this.state.emailAddress.value.trim(),
            rentTermsKey: this.props.rentTermsKey,
          });
        })
        .then((response = {}) => {
          if (response.error) {
            this.toggleIsLoading(false);
          }
        });
    }
    return false;
  };

  handleSubmitForRentalProtectionVariation = (e) => {
    e.preventDefault();
    const { selectedLease } = this.props;
    const { tenantId } = this.state;

    if (validateTenantSelected(this)) {
      return Promise.resolve()
        .then(() => this.toggleIsLoading(true))
        .then(() => {
          return this.props.onSubmit({
            tenantFirstName: selectedLease?.leaseTenants[tenantId.value].firstName.trim(),
            tenantLastName: selectedLease?.leaseTenants[tenantId.value].lastName.trim(),
            emailAddress: selectedLease?.leaseTenants[tenantId.value].email.trim(),
            rentTermsKey: this.props.rentTermsKey,
          });
        })
        .then((response = {}) => {
          if (response.error) {
            this.toggleIsLoading(false);
          }
        });
    }
    return false;
  };

  onCancel = (e) => {
    e.preventDefault();
    this.props.onCancel();
  };

  onClose = () => {
    this.props.onClose();
  };

  renderRentalProtectionVariation = () => {
    const { tenantId } = this.state;
    const tenantOptions = this.renderTenantOptions();

    return (
      <Section>
        <Label className="flexDisplay">
          <span>Select Tenant</span>
        </Label>
        <SpacerRow size="sm" />
        <Select data-id="tenantId" value={tenantId.value} error={tenantId.error} onChange={this.onChangeSelect}>
          {tenantOptions}
        </Select>
      </Section>
    );
  };

  renderDefault = () => {
    return (
      <>
        <Row>
          <Col>
            <Input
              data-statekey="tenantFirstName"
              className="fs-exclude"
              label="First name"
              value={this.state.tenantFirstName.value}
              error={this.state.tenantFirstName.error}
              onChange={this.onInputChangeName}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Input
              data-statekey="tenantLastName"
              className="fs-exclude"
              label="Last name"
              value={this.state.tenantLastName.value}
              error={this.state.tenantLastName.error}
              onChange={this.onInputChangeName}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Input
              data-statekey="emailAddress"
              className="fs-exclude"
              label="Email address"
              value={this.state.emailAddress.value}
              error={this.state.emailAddress.error}
              onChange={this.onInputChange}
            />
          </Col>
        </Row>
      </>
    );
  };

  render() {
    const { isLoading } = this.state;
    const { isRentGuaranteeEnabled, selectedLease } = this.props;
    const showTenantDropdown = isRentGuaranteeEnabled && selectedLease?.leaseTenants?.length > 0;
    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header={'TENANT INFO'}
        onRequestClose={this.onClose}
        onClickBack={null}
        editMode
        dataAutoTestId="landlord-invite-tenant"
      >
        {isLoading && <SpinnerContainer />}
        <Row>
          <Col>
            <Text type="h3">Who is your primary tenant?</Text>
            <Text type="body">Only enter the information of your primary tenant</Text>
          </Col>
        </Row>
        <form noValidate>
          {showTenantDropdown ? this.renderRentalProtectionVariation() : this.renderDefault()}
          <Row>
            <Col>
              <FaqComponent.Landlord faqs={FaqConstants.ONBOARDING_V2.INVITE_TENANT_PAGE} />
            </Col>
          </Row>
          <Row>
            <Col>
              <DebouncedButton
                type="primary"
                onClick={showTenantDropdown ? this.handleSubmitForRentalProtectionVariation : this.handleSubmit}
                full
              >
                Update
              </DebouncedButton>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button full data-statekey="Cancel" onClick={this.onCancel} type="tertiary">
                Cancel
              </Button>
            </Col>
          </Row>
        </form>
      </Modal>
    );
  }
}

export default EditTenantInfoModal;
